/**
 * apiFetch.js
 *
 * Wraps fetch() for calls to auth.ezron.ai Lambdas that rely on the HttpOnly
 * firebaseToken cookie (credentials:"include"). The cookie is minted from a
 * Firebase ID token that can go stale server-side — e.g. the tab was
 * backgrounded/asleep long enough that AppInitializer's proactive refresh
 * timer never got to run — even though the client-side Firebase Auth SDK
 * still reports the user as signed in. On a 401 we transparently mint a
 * fresh ID token, resync the cookie, and retry the request ONCE. Only if
 * that resync/retry also fails (refresh token itself revoked/expired) do we
 * force a real sign-out, since at that point silently retrying forever
 * would just mask a real "please log in again" state.
 */
import { getAuth } from "firebase/auth";
import { syncCurrentUserSession, signOutQuietly } from "./firebaseAuth";

let sessionExpiredHandled = false;

function forceSessionExpiredSignOut() {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  signOutQuietly().finally(() => {
    const returnUrl = window.location.pathname + window.location.search;
    window.location.href = `/login?reason=session-expired&returnUrl=${encodeURIComponent(returnUrl)}`;
  });
}

/**
 * @param {string} url Full request URL.
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
export async function apiFetch(url, options = {}) {
  const request = { credentials: "include", ...options };
  let res = await fetch(url, request);

  if (res.status === 401 && getAuth().currentUser) {
    try {
      const resynced = await syncCurrentUserSession();
      if (resynced) {
        res = await fetch(url, request);
      }
    } catch {
      // fall through — resync failed, treat like any other still-401 response below
    }
  }

  if (res.status === 401) {
    forceSessionExpiredSignOut();
  }

  return res;
}
